import {
  type ActivityLogItemDtoType,
  type ActivityLogResponseDtoType,
  type BatchTransitionRequestDtoType,
  type BatchTransitionResponseDtoType,
  type CreateUserBookmarkResponseDtoType,
  type NextPatientDtoType,
  type NotificationTemplateResponseDtoType,
  type PatientTrackingCountsRequestDtoType,
  type PatientTrackingCountsResponseDtoType,
  type PatientTrackingListRequestDtoType,
  type PatientTrackingListResponseDtoType,
  type SiteLocationDtoType,
  type SiteLocationsResponseDtoType,
  type TransitionNextPatientRequestDtoType,
} from '@tempus/trials-types';

import axios from '../axios';
import { NEXT_PATIENT_TRACKING, TRIALS_NEST_API_ROOT, getSelectedSiteId, omitUndefinedQueryParams } from '../constants';

export const nextPatientTrackingApi = {
  transitionNextPatient: async (body: TransitionNextPatientRequestDtoType): Promise<NextPatientDtoType> => {
    return axios.post(`${TRIALS_NEST_API_ROOT}/next-patient/transition`, body).then(({ data }) => data);
  },
  getActivityLog: async (nextPatientId: string, siteId?: string): Promise<ActivityLogResponseDtoType> => {
    return axios
      .get(`${NEXT_PATIENT_TRACKING}/patients/${nextPatientId}/activity-log`, {
        params: { siteId: siteId ?? getSelectedSiteId() },
      })
      .then(({ data }) => data);
  },
  getNotificationTemplate: async (params: {
    siteId: string;
    carePathwayStageId: string;
    nextPatientId: string;
    notifiedPhysicianId: string;
  }): Promise<NotificationTemplateResponseDtoType> => {
    return axios
      .get(`${NEXT_PATIENT_TRACKING}/notification-template`, {
        params: {
          carePathwayStageId: params.carePathwayStageId,
          nextPatientId: params.nextPatientId,
          notifiedPhysicianId: params.notifiedPhysicianId,
          siteId: params.siteId,
        },
      })
      .then(({ data }) => data);
  },
  createActivityNote: async (nextPatientId: string, note: string, siteId?: string): Promise<ActivityLogItemDtoType> => {
    return axios
      .post(`${TRIALS_NEST_API_ROOT}/next-patient/${nextPatientId}/activity-note`, {
        note,
        siteId: siteId ?? getSelectedSiteId(),
      })
      .then(({ data }) => data);
  },
  getPatients: async (params: PatientTrackingListRequestDtoType): Promise<PatientTrackingListResponseDtoType> => {
    return axios
      .get(`${NEXT_PATIENT_TRACKING}/patients`, {
        params: { ...params, siteId: params.siteId ?? getSelectedSiteId() },
      })
      .then(({ data }) => data);
  },
  getPatientCounts: async (
    params: PatientTrackingCountsRequestDtoType,
  ): Promise<PatientTrackingCountsResponseDtoType> => {
    const requestParams = omitUndefinedQueryParams({
      ...params,
      siteId: params.siteId || getSelectedSiteId() || undefined,
    });

    return axios
      .get(`${NEXT_PATIENT_TRACKING}/patients/counts`, {
        params: requestParams,
      })
      .then(({ data }) => data);
  },
  getLocations: async (siteId?: string): Promise<SiteLocationDtoType[]> => {
    return axios
      .get<SiteLocationsResponseDtoType>(`${NEXT_PATIENT_TRACKING}/locations`, {
        params: { siteId: siteId ?? getSelectedSiteId() },
      })
      .then(({ data }) => data.locations);
  },
  createBookmark: async (stageTrackerId: string, siteId?: string): Promise<CreateUserBookmarkResponseDtoType> => {
    return axios
      .post(`${NEXT_PATIENT_TRACKING}/stage-trackers/${stageTrackerId}/bookmark`, {
        siteId: siteId ?? getSelectedSiteId(),
      })
      .then(({ data }) => data);
  },
  deleteBookmark: async (stageTrackerId: string, siteId?: string): Promise<void> => {
    return axios
      .delete(`${NEXT_PATIENT_TRACKING}/stage-trackers/${stageTrackerId}/bookmark`, {
        params: { siteId: siteId ?? getSelectedSiteId() },
      })
      .then(() => undefined);
  },
  updateStageTrackerNotes: async (stageTrackerId: string, notes: string, siteId?: string): Promise<void> => {
    await axios.put(`${NEXT_PATIENT_TRACKING}/stage-trackers/${stageTrackerId}`, {
      notes,
      siteId: siteId ?? getSelectedSiteId(),
    });
  },
  transitionNextPatientTracking: async (
    body: BatchTransitionRequestDtoType,
  ): Promise<BatchTransitionResponseDtoType> => {
    return axios.put(`${NEXT_PATIENT_TRACKING}/transition`, body).then(({ data }) => data);
  },
};
